/**
 * Role-based nav visibility. Admin's NAV is intentionally scoped to admin
 * consoles + shared tools; admins can still OPEN other role workspaces by URL
 * (RoleRoute in App.jsx keeps 'admin' in its allow-lists) — the menu just
 * stops advertising every persona's workspace at once. Mirrors the RoleRoute guards in App.jsx so the top
 * navigation never advertises a workspace the signed-in role can't open (an
 * employer, for example, should not see Academy Console, Course Studio, or
 * Coupons & Payments).
 *
 * Only role-GATED slugs are listed here. Slugs not listed are reachable by any
 * authenticated user (ProtectedRoute) or are public (catalogue, verifier), so
 * they stay visible for everyone. A parent menu entry is shown when at least one
 * of its children is visible.
 */
export const NAV_ROLE_GATES = {
  'academy-operations-console': ['admin', 'leadership'],
  'roles-permissions-approvals-dashboard': ['admin'],
  'certification-criteria-manager': ['admin'],
  'graduate-record-administrator': ['admin'],
  'coupon-payment-admin-console': ['admin'],
  'job-board-moderation-console': ['admin'],
  'platform-settings': ['admin'],
  'employer-operations-workspace': ['employer'],
  'role-landing-hub': ['mentor', 'course supervisor', 'leadership', 'admin'],
  'cohort-communications-announcements': ['mentor', 'course supervisor', 'leadership', 'admin'],
  'academic-design-agent-console': ['academic design agent', 'course architect', 'admin'],
  'learner-feedback-insights-workspace': ['academic design agent', 'course architect', 'admin'],
  'learner-management-agent-dashboard': [
    'learner course management agent',
    'mentor',
    'course supervisor',
    'leadership',
    'admin',
  ],
  // Learner tools — only learners (and admin) see these in the nav.
  'assessment-center': ['user'],
  'course-player': ['user'],
  'graduate-job-board': ['user'],
  // Authoring tools — course architects / design agents (and admin).
  'module-composer': ['course architect', 'academic design agent', 'admin'],
};

// Slugs reachable WITHOUT logging in (public routes in App.jsx). Everything not
// listed here and not in NAV_ROLE_GATES is a ProtectedRoute — visible only once
// authenticated.
export const PUBLIC_SLUGS = new Set(['course-catalogue', 'credential-verification-portal']);

// True when the current visitor may open the given slug:
//  - public slugs           → always
//  - role-gated slugs        → authenticated AND role in the allow-list
//  - any other (auth-only)   → authenticated
export function canAccessSlug(slug, role, isAuthenticated) {
  if (!slug) return true; // parent nodes are decided by their children
  if (PUBLIC_SLUGS.has(slug)) return true;
  const allowed = NAV_ROLE_GATES[slug];
  if (allowed) return Boolean(isAuthenticated) && allowed.includes(role);
  return Boolean(isAuthenticated);
}

// Return a filtered copy of the menu tree: hidden leaves are dropped and a parent
// with no visible children is removed entirely.
export function filterMenuByRole(items, role, isAuthenticated) {
  return items
    .map((item) => {
      if (Array.isArray(item.children) && item.children.length) {
        const children = filterMenuByRole(item.children, role, isAuthenticated);
        return children.length ? { ...item, children } : null;
      }
      return canAccessSlug(item.slug, role, isAuthenticated) ? item : null;
    })
    .filter(Boolean);
}
